import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";

import GlassLayout from "layouts/authentication/components/GlassLayout";
import { API_BASE } from "config/api";

function Register() {
  const { churchId, churchName } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    church_id: churchId || "",
    name: churchName ? decodeURIComponent(churchName) : "",
    email: "",
    password: "",
    password_confirmation: "",
    role: "admin",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/register`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) setErrors(data.errors);
        throw new Error(data.message || "Registration failed");
      }

      setOpenSuccess(true);
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (error) {
      setOpenError(true);
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GlassLayout maxWidth="sm" eyebrow="One last step — create the admin login.">
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center">
          Create Admin Account
        </Typography>
        {churchName && (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 0.5, mb: 3 }}
          >
            For {decodeURIComponent(churchName)}
          </Typography>
        )}

        <Box component="form" role="form" onSubmit={handleSubmit} sx={{ mt: churchName ? 0 : 3 }}>
          <TextField
            type="text"
            name="name"
            label="Church"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name?.[0]}
            disabled={!!churchName}
            sx={{ mb: 2 }}
          />
          <input type="hidden" name="church_id" value={formData.church_id} />

          <TextField
            type="email"
            name="email"
            label="Email"
            fullWidth
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email?.[0]}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            type="password"
            name="password"
            label="Password"
            fullWidth
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password?.[0]}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            type="password"
            name="password_confirmation"
            label="Confirm Password"
            fullWidth
            value={formData.password_confirmation}
            onChange={handleChange}
            error={!!errors.password_confirmation}
            helperText={errors.password_confirmation?.[0]}
            required
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ mt: 2, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            {isSubmitting ? "Creating Account..." : "Create Account"}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={openSuccess}
        autoHideDuration={6000}
        onClose={() => setOpenSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Registration successful! Next time, please login with your email and password.
        </Alert>
      </Snackbar>

      <Snackbar
        open={openError}
        autoHideDuration={6000}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </GlassLayout>
  );
}

export default Register;
